"use strict";

import * as olProj from 'ol/proj';
import Style from "ol/style/Style";
import * as olGeom from 'ol/geom';
import * as olSphere from 'ol/sphere';

/**
 * @classdesc
 * Utilities zur Nutzung der Openlayers Kartenbibliothek.
 *
 * @class
 * @memberof gc.gis
 * @author Christian Schmidt <c.schmidt@brain-scc.de>
 */
gc.gis.OLUtil = {

	/**
	 * WGS84 Projekttionsobjekt.
	 *
	 * @type {ol.proj.Projection}
	 * @private
	 */
	_wgs84_proj: olProj.get('EPSG:4326'),

	/**
	 * Kugelflächenimplementierung für Flächenbestimmung bei nicht metrischen CRS.
	 * In OL v4.3.0 #7013: - Remove olSphere.WGS84 and olSphere.NORMAL
	 *
	 * Erdradius als Parameter für olSphere Methoden:
	 * - GRS 80, WGS 84 (Jahr 1979) - Äquatorradius: 6378137m
	 * - GRS 80, WGS 84 (Jahr 1979) - Mittelwert: 6371008.8m (in OL 5.0.0 default - Sphere radius. By default, the radius of the earth is used (Clarke 1866 Authalic Sphere))
	 * Quelle: https://de.wikipedia.org/wiki/Erdradius
	 * Der Radius kann nicht exakt angegeben werden, da die Erde keine perfekte Kugel ist hinzu kommt in Abhängigkeit
	 * des aktuellen Standorts eigentlich auch noch die Höhe über dem Meeresspiegel.
	 * Online-Rechner siehe: https://rechneronline.de/erdradius/
	 *
	 * @type {number}
	 * @private
	 */
	_wgs84_radius: 6378137, // 6371008.8,

    /**
     * Konvertiert eine Liste von Styles in ein Openlayers Style-Objekt.
     *
     * @param {gc.gis.style.GCAbstractStyle[]|Function|ol.style.Style} styles
     * @return {ol.style.Style|ol.StyleFunction}
     */
    toOLStyle: function(styles)
    {
        if (typeof styles === 'function' || styles instanceof Style)
        {
            return styles;
        }

        const style_opts = {};
        if (!Array.isArray(styles))
        {
            styles = [styles];
        }

        // TODO ggf. weitere Varianten der Übergabe implementieren (z.B. Icon)
        for (let i=0; i<styles.length; i++)
        {
            let style = styles[i];
            if (style instanceof gc.gis.style.GCFill) {
                style_opts.fill = style.getImpl();
            }
            if (style instanceof gc.gis.style.GCStroke) {
                style_opts.stroke = style.getImpl();
            }
            if (style instanceof gc.gis.style.GCCircle) {
                style_opts.image = style.getImpl();
            }
        }

        return new Style(style_opts);
    },

	/**
     * Überführt die Koordinaten der Geometrie in eine flache Liste.
     *
	 * @param {ol.geom.SimpleGeometry} geometry
     * @return {Array}
	 */
	getFlatCoordinates: function(geometry)
    {
        let flatCoords = [];

	    if (geometry instanceof olGeom.Circle)
        {
            // TODO
	        return flatCoords;
        }
        else if (geometry instanceof olGeom.GeometryCollection)
        {
            // TODO
            return flatCoords;
        }

	    let coords = geometry.getCoordinates(),
	        i, ii, j, jj, k, kk;

        if (geometry instanceof olGeom.Point)
        {
	        flatCoords = flatCoords.concat(coords); // Klonen
        }
	    else if (geometry instanceof olGeom.LineString
            || geometry instanceof olGeom.LinearRing
            || geometry instanceof olGeom.MultiPoint)
        {
            for (i=0, ii=coords.length; i<ii; i++)
            {
	            flatCoords = flatCoords.concat(coords[i]);
            }
        }
        else if (geometry instanceof olGeom.Polygon
            || geometry instanceof olGeom.MultiLineString)
        {
	        for (i=0, ii=coords.length; i<ii; i++)
	        {
		        for (j=0, jj=coords[i].length; j<jj; j++)
                {
	                flatCoords = flatCoords.concat(coords[i][j]);
                }
	        }
        }
        else if (geometry instanceof olGeom.MultiPolygon)
        {
	        for (i=0, ii=coords.length; i<ii; i++)
	        {
		        for (j=0, jj=coords[i].length; j<jj; j++)
		        {
			        for (k=0, kk=coords[i][j].length; k<kk; k++)
			        {
				        flatCoords = flatCoords.concat(coords[i][j][k]);
			        }
		        }
	        }
        }

        return flatCoords;
    },


	/**
	 * Prüft, ob die angegebene Koordinate ein Stützpunkt der Geometrie ist.
	 *
	 * @public
	 * @method
	 *
	 * @param {ol.Coordinate} coord
	 * @param {ol.geom.SimpleGeometry} geometry
	 * @return {boolean}
	 */
	isVertex: function(coord, geometry)
	{
		// coords = geometry.getFlatCoordinates();
		const coords = gc.gis.OLUtil.getFlatCoordinates(geometry);
		let i;

		for (i = coords.length - 1; i > 0; i--)
		{
			if (coord[0] === coords[i-1] && coord[1] === coords[i])
			{
				return true;
			}
		}

		return false;
	},

	/**
	 * Prüft, ob die übergebene Projektion metrische Koordinaten benutzt.
	 *
	 * @public
	 * @method
	 *
	 * @param {ol.proj.Projection|string} projection
	 * @return {boolean}
	 * @throws {gc.gis.error.GCError}
	 */
	isMetricProjection: function(projection)
	{
		if(!(typeof projection === 'string'))
		{
			projection = olProj.get(projection);
		}

		if (!projection instanceof olProj.Projection)
		{
			throw new gc.gis.error.GCError('Koordinatensystem unbekannt.');
		}

		let units = projection.getUnits();
		if (!units)
		{
			throw new gc.gis.error.GCError('Maßeinheit im Koordinatensystem "' + projection.getCode() + '" unbekannt.');
		}

		units = units.toLowerCase();
		return units === 'm' || units === 'meters' || units === 'meter';
	},

	/**
	 * Berechnet (<strong>client-seitig!</strong>) die Länge zwischen zwei Koordinaten. Wenn es sich um ein metrisches
	 * Koordinatenreferenzsystem handelt, wird die euklidische Distanz zurückgegeben, ansonsten werden die Koordinaten
	 * ggf. in geografische transformiert und die ellipsoidische Distanz ermittelt.
	 *
	 * @public
	 * @method
	 *
	 * @param {ol.Coordinate} c1
	 * @param {ol.Coordinate} c2
	 * @param {ol.proj.Projection} projection
	 * @return {Number}
	 */
	calcLength: function(c1, c2, projection)
	{
		let distance = 0.0,
			geom;

		if (!this.isMetricProjection(projection))
		{
			if (projection.getCode() !== this._wgs84_proj.getCode())
			{
				c1 = olProj.transform(c1, projection, this._wgs84_proj);
				c2 = olProj.transform(c2, projection, this._wgs84_proj);
			}

			// ehemals olSphere.haversineDistance leider steht ol.ellipsoid.WGS84.vincentyDistance nicht mehr zur Verfügung
			distance = olSphere.getDistance(c1, c2);
		}
		else
		{
			geom = new olGeom.LineString([c1, c2]);
			distance = geom.getLength();
		}

		return distance;
	},

	/**
	 * Berechnet (<strong>client-seitig!</strong>) den Flächeninhalt 3er oder mehr Koordinaten. Wenn es sich um ein
	 * metrisches Koordinatenreferenzsystem handelt, wird die euklidische Fläche zurückgegeben, ansonsten werden die
	 * Koordinaten ggf. in geografische transformiert und die ellipsoidische Fläche ermittelt.
	 *
	 * @public
	 * @method
	 *
	 * @param {ol.Coordinate[]} coords Drei oder mehr Koordinaten
	 * @param {ol.proj.Projection} projection
	 * @return {Number} Flächeninhalt
	 */
	calcArea: function(coords, projection)
	{
		if (coords.length < 3)
		{
			return 0.0;
		}

		let i, ii, geom;

		if (!this.isMetricProjection(projection))
		{
			if (projection.getCode() !== this._wgs84_proj.getCode())
			{
				for(i = 0, ii = coords.length; i < ii; i++)
				{
					coords[i] = olProj.transform(coords[i], projection, this._wgs84_proj);
				}
			}

			// ehemals getArea() ehemals olSphere.geodesicArea()
			return Math.abs(olSphere.getArea(new olGeom.Polygon([coords]), {
				projection: this._wgs84_proj,
				radius: this._wgs84_radius
			}));
		}

		geom = new olGeom.Polygon([coords]);
		return geom.getArea();
	}
};